/**
 * Build and open portal-v2 URLs with synced shared session cookies + projectId.
 */

import { getMainPortalBaseUrl, joinMainPortalPath } from './config';
import {
  getPortalSessionSnapshot,
  syncSharedSessionCookies,
} from './portalSession';

export type PortalAppUrlQuery = Record<string, string | number | boolean | null | undefined>;

export type BuildPortalAppUrlOptions = {
  /** Override active project; defaults to current portal session project. */
  projectId?: string;
  /** Extra query params (ticketId, etc.). `projectId` here is overridden by options.projectId. */
  query?: PortalAppUrlQuery;
};

function appendQuery(url: string, query: PortalAppUrlQuery): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined || value === '') {
      continue;
    }
    params.set(key, String(value));
  }
  const qs = params.toString();
  if (!qs) {
    return url;
  }
  return url.includes('?') ? `${url}&${qs}` : `${url}?${qs}`;
}

/**
 * Build an absolute portal-v2 URL. Accepts a path (`/requests/...`), query-only
 * (`?ticketId=1`), or a full URL. Always injects `projectId` when known.
 */
export function buildPortalAppUrl(
  pathOrUrl: string,
  options: BuildPortalAppUrlOptions = {},
): string {
  const snapshot = getPortalSessionSnapshot();
  const projectId =
    String(options.projectId ?? snapshot?.projectId ?? '').trim() || undefined;

  const query: PortalAppUrlQuery = {
    ...(options.query ?? {}),
    ...(projectId ? { projectId } : {}),
  };

  const target = (pathOrUrl || '/').trim();
  let baseUrl: string;

  if (/^https?:\/\//i.test(target)) {
    baseUrl = target;
  } else if (target.startsWith('?')) {
    const portalBase = getMainPortalBaseUrl().replace(/\/+$/, '');
    if (!portalBase) {
      return '';
    }
    baseUrl = `${portalBase}/${target}`;
  } else {
    baseUrl = joinMainPortalPath(target.startsWith('/') ? target.slice(1) : target);
  }

  if (!baseUrl) {
    return '';
  }

  // If path already had its own query, merge via URL when possible
  try {
    const parsed = new URL(baseUrl);
    for (const [key, value] of Object.entries(query)) {
      if (value === null || value === undefined || value === '') {
        continue;
      }
      // Do not overwrite an explicit projectId already on the URL unless options force it
      if (key === 'projectId' && parsed.searchParams.has('projectId') && !options.projectId) {
        continue;
      }
      parsed.searchParams.set(key, String(value));
    }
    if (projectId && options.projectId) {
      parsed.searchParams.set('projectId', projectId);
    } else if (projectId && !parsed.searchParams.has('projectId')) {
      parsed.searchParams.set('projectId', projectId);
    }
    return parsed.toString();
  } catch {
    return appendQuery(baseUrl, query);
  }
}

export type OpenPortalUrlOptions = BuildPortalAppUrlOptions & {
  /** Default: new tab. Use 'assign' for same-tab navigation. */
  target?: 'blank' | 'assign';
};

/**
 * Sync shared SSO cookies, then open a portal URL with projectId on the query string.
 */
export function openPortalUrl(
  pathOrUrl: string,
  options: OpenPortalUrlOptions = {},
): boolean {
  syncSharedSessionCookies();
  const url = buildPortalAppUrl(pathOrUrl, options);
  if (!url || typeof window === 'undefined') {
    console.warn('[portalNavigation] Missing portal URL or window');
    return false;
  }

  if (options.target === 'assign') {
    window.location.assign(url);
    return true;
  }

  window.open(url, '_blank', 'noopener,noreferrer');
  return true;
}
